import React, { memo } from 'react';
import { MarkerF } from '@react-google-maps/api';
import { CustomPlace, getLatLng } from '../../types/interfaces';
import { PlaceMarker, PlaceMarkerProps } from './PlaceMarker';


interface MapMarkerProps {
  customPlace: CustomPlace;
  isUser?: boolean;
  useGlassmorphic?: boolean; // Option to use new glassomorphic markers
  isSelected?: boolean;
  isHovered?: boolean;
  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const getIconUrl = (marker: CustomPlace): string => {
  // We could use types, but they need to fit within those types allowable in this big list:
  // https://developers.google.com/maps/documentation/places/web-service/supported_types
  // Note that types are not mutually exclusive. A place may have both food and bar types.
  //
  // We'll do price range for now: Free, Inexpensive, Moderate, Expensive, Very Expensive
  // let logo_path = `/logo-red.png`;
  // let colors = ['green', 'yellow', 'magenta', 'purple', 'red']
  // if (marker.price_level) logo_path = `/logo-${colors[marker.price_level]}.png`;

  return '/marker.png'
}

const MarkerIcon = (isUser: boolean) => {
  const svg = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="12" fill="${isUser ? '#00FFFF' : 'rgba(0, 255, 255, 0.2)'}" />
        <circle cx="12" cy="12" r="6" fill="#00FFFF" />
      </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export const MapMarker: React.FC<MapMarkerProps> = ({
  customPlace,
  isUser,
  useGlassmorphic = true, // Default to true for new glassomorphic markers
  isSelected,
  isHovered,
  isLoading,
  size = 'medium',
  onClick,
  onMouseEnter,
  onMouseLeave
}) => {
  // For user location markers, always use the original cyan circle
  if (isUser) {
    return (
      <MarkerF
        position={getLatLng(customPlace)}
        onClick={onClick}
        title={customPlace.name}
        icon={{
          url: MarkerIcon(true),
          scaledSize: new google.maps.Size(24, 24),
          anchor: new google.maps.Point(12, 12),
        }}
      />
    );
  }

  // Use new glassomorphic markers for places (unless explicitly disabled)
  if (useGlassmorphic) {
    return (
      <PlaceMarker
        place={customPlace}
        isSelected={isSelected}
        isHovered={isHovered}
        isLoading={isLoading}
        size={size}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  }

  // Fallback to original marker style
  return (
    <MarkerF
      position={getLatLng(customPlace)}
      onClick={onClick}
      title={customPlace.name}
      icon={{
        url: MarkerIcon(false),
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
      }}
    />
  );
};

export const MemoizedMapMarker = memo(({
  place,
  onClick,
  useGlassmorphic = true,
  isSelected,
  isHovered,
  isLoading,
  size = 'medium'
}: {
  place: CustomPlace;
  onClick: () => void;
  useGlassmorphic?: boolean;
  isSelected?: boolean;
  isHovered?: boolean;
  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';
}) => {
  if (useGlassmorphic) {
    return (
      <PlaceMarker
        key={place.placeId}
        place={place}
        isSelected={isSelected}
        isHovered={isHovered}
        isLoading={isLoading}
        size={size}
        onClick={onClick}
      />
    );
  }

  // Fallback to original implementation
  return (
    <MarkerF
      key={place.placeId}
      position={getLatLng(place)}
      onClick={onClick}
      title={place.name}
      icon={{ url: getIconUrl(place), scaledSize: new google.maps.Size(24, 24) }}
    />
  );
});
